import { z } from 'zod';
import type { TipoAutorizacion } from '../permisos.ts';
import { METODOS_PAGO_ENTRADA, METODOS_VUELTO_BS, type MetodoVueltoBs } from '../tipos.ts';
import { autorizacionesAdjuntas } from './autorizaciones.ts';
import { comboEnVentaInput, type ComboEnDocumentoDTO } from './combos.ts';
import { decimalNoNegativo, decimalPositivo, paginacion, uuid } from './comun.ts';
import type { PlantillaFactura } from './impresion.ts';

export const lineaVentaInput = z.object({
  producto_id: uuid,
  presentacion_id: uuid,
  /** Cantidad de presentaciones (ej. 2 cajas de 12). */
  cantidad: decimalPositivo,
  /** Con permiso `ventas.descuento` o con una autorización de tipo `descuento` adjunta a la venta. */
  descuento_pct: decimalNoNegativo.refine((v) => Number(v) <= 100, 'El descuento no puede superar 100 %').optional(),
  /**
   * Almacén del que sale el renglón. Sin indicarlo: la tienda (almacén principal). Un depósito exige el permiso
   * `ventas.vender-deposito` o una autorización de tipo `venta_deposito`.
   */
  almacen_id: uuid.optional(),
  /** Pieza de vidrio que se vende en este renglón (cantidad 1; el precio es el de la pieza). */
  pieza_id: uuid.optional(),
  /** Combo o receta al que pertenece el renglón (spec 20): el grupo de `combos`. */
  combo_grupo: uuid.optional(),
});
export type LineaVentaInput = z.infer<typeof lineaVentaInput>;

export const pagoLineaInput = z.object({
  metodo: z.enum(METODOS_PAGO_ENTRADA),
  /** Monto en la moneda del método (Bs para efectivo_bs/punto/transferencia/pago_movil; $ para el resto). */
  monto: decimalPositivo,
  referencia: z.string().trim().max(60).nullable().optional(),
  banco: z.string().trim().max(60).nullable().optional(),
  /**
   * Pago móvil o transferencia confirmado con Banesco (spec 19): la verificación que lo respalda. El servidor exige el
   * mismo monto, usa la referencia que dio el banco y la consume (un movimiento del banco paga un solo cobro).
   */
  verificacion_banco_id: z.uuid('Verificación inválida').nullable().optional(),
});
export type PagoLineaInput = z.infer<typeof pagoLineaInput>;

/**
 * Vuelto que el cajero entrega (respuesta de la clienta 2026-10-09): cuánto en dólares y cuánto en bolívares, y cómo
 * salieron los bolívares. Lo que sobre del excedente y no se devuelva queda como "sobrante por vuelto". Si no se
 * manda, el sistema reparte todo el excedente (primero $ del efectivo recibido, el resto en Bs efectivo).
 */
export const vueltoInput = z.object({
  usd: decimalNoNegativo.default('0'),
  bs: decimalNoNegativo.default('0'),
  bs_metodo: z.enum(METODOS_VUELTO_BS).default('efectivo_bs'),
  bs_referencia: z.string().trim().max(60).nullable().optional(),
});
export type VueltoInput = z.infer<typeof vueltoInput>;
export type VueltoEntrada = z.input<typeof vueltoInput>;

export const ventaInput = z.object({
  tipo: z.enum(['FAC', 'NOTA']),
  cliente_id: uuid,
  lineas: z.array(lineaVentaInput).min(1, 'Agrega al menos un producto').max(200),
  /** Combos y recetas del carrito (spec 20): el servidor los recalcula y solo con renglones iguales aplica su descuento. */
  combos: z.array(comboEnVentaInput).max(20).optional(),
  condicion: z.enum(['contado', 'credito']).default('contado'),
  /** Descuento global %: con permiso `ventas.descuento` o autorización de tipo `descuento`. */
  descuento_pct: decimalNoNegativo.refine((v) => Number(v) <= 100, 'El descuento no puede superar 100 %').optional(),
  /** Cliente especial en FAC: obligatorio elegir (spec 05 regla 6). */
  modalidad_retencion: z.enum(['base25', 'completo']).optional(),
  /** Contado: pagos que cubren el por_cobrar. Crédito: vacío. */
  pagos: z.array(pagoLineaInput).max(20).default([]),
  /** Vuelto entregado (si hubo excedente); sin esto se reparte solo. */
  vuelto: vueltoInput.optional(),
  /** Vender a crédito por encima del límite: con permiso `clientes.credito` o autorización de tipo `credito` (queda auditado). */
  override_credito: z.object({ motivo: z.string().trim().min(3).max(300) }).optional(),
  /** Autorizaciones aprobadas que acompañan la venta (descuento, crédito, cliente vencido, venta contra depósito…). */
  autorizaciones: autorizacionesAdjuntas,
  observaciones: z.string().trim().max(500).optional(),
  /** Espera que se procesa con esta emisión (se marca procesada). */
  espera_id: uuid.optional(),
  cotizacion_id: uuid.optional(),
});
export type VentaInput = z.infer<typeof ventaInput>;
export type VentaEntrada = z.input<typeof ventaInput>;

/** Cálculo sin emitir (carrito): mismos precios/tasas/retenciones que la emisión. */
export const calcularVentaInput = ventaInput.pick({ tipo: true, cliente_id: true, lineas: true, combos: true, descuento_pct: true, modalidad_retencion: true, condicion: true });
export type CalcularVentaInput = z.infer<typeof calcularVentaInput>;

export const pagoDocumentoInput = z.object({
  pagos: z.array(pagoLineaInput).min(1).max(20),
  vuelto: vueltoInput.optional(),
  observaciones: z.string().trim().max(300).optional(),
});
export type PagoDocumentoInput = z.infer<typeof pagoDocumentoInput>;
export type PagoDocumentoEntrada = z.input<typeof pagoDocumentoInput>;

export const abonoInput = z.object({
  cliente_id: uuid,
  pagos: z.array(pagoLineaInput).min(1).max(20),
  observaciones: z.string().trim().max(300).optional(),
});
export type AbonoInput = z.infer<typeof abonoInput>;

/** Hoja dañada: anula número y control y reemite con los siguientes (permiso `ventas.anular` o autorización). */
const hojaDanadaInput = z.object({
  motivo: z.string().trim().min(5, 'Explica qué pasó con la hoja (mínimo 5 caracteres)').max(300),
  autorizaciones: autorizacionesAdjuntas,
});
export const reemitirInput = hojaDanadaInput.extend({
  /**
   * Número que muestra la pantalla desde la que se pide. Si el documento ya tiene otro (otra pantalla lo reemitió), se
   * rechaza con 409 `documento_ya_reemitido`: una hoja dañada consume UN número (auditoría 2026-10-11, H-15).
   * Obligatorio: sin él, una pantalla vieja o cualquier otro cliente se saltaría la protección.
   */
  numero_actual: z.string().trim().min(1).max(30),
});
export type ReemitirInput = z.infer<typeof reemitirInput>;
/** Hoja en blanco dañada: se pierde el número de control sin documento. */
export const anularControlInput = hojaDanadaInput;
export type AnularControlInput = z.infer<typeof anularControlInput>;

/** Estado del talonario de papel fiscal de la sucursal, para avisar en el punto de venta ANTES de cobrar (H-04). */
export interface PapelFiscalDTO {
  /** false = la sucursal no cargó el rango de su talonario: el sistema numera de corrido y no puede avisar. */
  con_talonario: boolean;
  /** Hojas que quedan en el talonario en uso (null sin talonario). 0 = agotado: no se puede facturar. */
  restantes: number | null;
  desde: string | null;
  hasta: string | null;
}

export const esperaInput = z.object({
  nombre: z.string().trim().min(1).max(80),
  payload: calcularVentaInput.extend({ observaciones: z.string().trim().max(500).optional() }),
});
export type EsperaInput = z.infer<typeof esperaInput>;

export const ventasFiltros = paginacion.extend({
  q: z.string().trim().max(80).optional(),
  tipo: z.enum(['FAC', 'NOTA', 'NC', 'ND', 'CAM']).optional(),
  cliente_id: uuid.optional(),
  estado_pago: z.enum(['pendiente', 'parcial', 'pagado']).optional(),
  estado_entrega: z.enum(['pendiente', 'parcial', 'entregado', 'no_aplica']).optional(),
  /** `anulado` lista solo las filas anuladas por hoja dañada. */
  estado: z.enum(['emitido', 'anulado']).optional(),
  desde: z.iso.date().optional(),
  hasta: z.iso.date().optional(),
});
export type VentasFiltros = z.infer<typeof ventasFiltros>;

// ─── DTOs ───────────────────────────────────────────────────────────────────
export interface LineaCalculadaDTO {
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  producto_unidad: string;
  presentacion_nombre: string;
  unidades_presentacion: string;
  cantidad: string;
  unidades: string;
  precio_unitario_divisa: string;
  /** $ reales por unidad (= precio de lista; regla de precios 2026-10-09). */
  precio_unitario: string;
  /** Bs por unidad = round2(precio × tasa 2): lo que ve el cliente en el papel. */
  precio_unitario_bs: string;
  descuento_pct: string;
  subtotal: string;
  /** Bs de la línea = round2(precio_bs × unidades) − descuento de línea en Bs. */
  subtotal_bs: string;
  exenta: boolean;
  /** Disponible en el almacén del renglón. */
  disponible: string;
  /** true si el disponible actual no cubre las unidades. */
  falta: boolean;
  /** Almacén del que sale el renglón (null en servicios y piezas: no llevan inventario). */
  almacen_id: string | null;
  almacen_nombre: string | null;
  /** true = el renglón sale de un depósito, no de la tienda (venta contra depósito). */
  de_deposito: boolean;
  /** Servicio o pieza de vidrio: no reserva ni descuenta inventario. */
  sin_inventario: boolean;
  pieza_id: string | null;
  /** Descuento programado por la dueña que se aplicó solo (no requiere autorización). */
  descuento_programado: { id: string; nombre: string; porcentaje: string } | null;
  /** Combo o receta del renglón (spec 20) y el descuento que trae por venderse en el combo (null si no está en uno). */
  combo_grupo: string | null;
  descuento_combo: string | null;
  /** Otros almacenes de la sucursal con disponible (para vender contra depósito cuando la tienda no alcanza). */
  alternativas: { almacen_id: string; almacen_nombre: string; tipo: 'tienda' | 'deposito'; disponible: string }[];
}

export interface TotalesVentaDTO {
  bruto: string;
  descuento: string;
  base: string;
  exento: string;
  iva_pct: string;
  iva: string;
  total: string;
  /** Bolívares fiscales calculados por línea: base + exento + IVA = total (cuadra a mano). */
  bruto_bs: string;
  descuento_bs: string;
  base_bs: string;
  exento_bs: string;
  iva_bs: string;
  total_bs: string;
  /** Tasa 2 (paralela) del documento: $ × tasa_aplicada ≈ Bs (FAC y NOTA). */
  tasa_aplicada: string;
  retencion_iva: string;
  retencion_municipal: string;
  /** Retenciones en Bs sobre los Bs fiscales (75 % del IVA en Bs; % municipal de la base en Bs). */
  retencion_iva_bs: string;
  retencion_municipal_bs: string;
  retencion_municipal_pct: string;
  por_cobrar: string;
  por_cobrar_bs: string;
  /** NOTA: máximo de Bs aceptable (tope 20$ a paralela). null en FAC. */
  tope_bs: string | null;
  /**
   * FACTURA de contado (regla del 2026-10-15): la misma factura al precio real a tasa BCV, para cuando el cliente paga
   * con divisas y el resto (precio sin IVA − divisas) no pasa del tope. `precio_sin_iva` es el precio real sin IVA de
   * toda la factura, en $; con él y lo que entrega en divisas se decide la regla (`modoPrecioFactura`). null en notas.
   */
  divisa: TotalesDivisaDTO | null;
}

/** Totales de la factura cuando sale al precio real a tasa BCV (cobro con divisas y resto dentro del tope). */
export interface TotalesDivisaDTO {
  precio_sin_iva: string;
  tope_resto: string;
  tasa_bcv: string;
  bruto_bs: string;
  descuento_bs: string;
  base_bs: string;
  exento_bs: string;
  iva_bs: string;
  total_bs: string;
  retencion_iva_bs: string;
  retencion_municipal_bs: string;
  por_cobrar_bs: string;
  /** Bs de cada renglón al precio real a tasa BCV (mismo orden que `lineas`): la pantalla de cobro los muestra. */
  lineas: { precio_unitario_bs: string; subtotal_bs: string }[];
}

export interface CalculoVentaDTO {
  tasas: { tasa_bcv: string; tasa_paralela: string; factor_divisa: string };
  lineas: LineaCalculadaDTO[];
  /** Combos y recetas de la venta, como los calculó el servidor. */
  combos: ComboEnDocumentoDTO[];
  totales: TotalesVentaDTO;
  /** NOTA: lo que el cliente ya pagó hoy en Bs y lo que le queda (tope por cliente y día); null en facturas. */
  bs_notas: BsNotasHoyDTO | null;
  /** vencido_usd: saldo de documentos a crédito ya vencidos (bloquea toda venta salvo permiso o autorización). */
  cliente: { id: string; razon_social: string; rif: string; lista_codigo: string; tipo_contribuyente: string; municipal: boolean; saldo_a_favor: string; apto_factura: boolean; apto_nota: boolean; vencido_usd: string; condicion: string; credito_disponible: string };
  requiere_modalidad: boolean;
  advertencias: string[];
  /** Lo que este usuario NO puede hacer por sí mismo en esta venta y exige autorización al emitir. */
  autorizaciones_requeridas: TipoAutorizacion[];
}

export interface PagoLineaDTO {
  id: string;
  metodo: string;
  moneda: 'USD' | 'BS';
  monto: string;
  monto_usd: string;
  tasa_aplicada: string | null;
  referencia: string | null;
  banco: string | null;
  /** no_aplica · pendiente (Zelle/Binance sin precargar) · verificado_precarga · verificado_manual · verificado_api (confirmado con Banesco, spec 19). */
  verificacion: string;
  /** Nombre de quien envió el Zelle/Binance, cuando coincidió con uno precargado. */
  pagador?: string | null;
}

/** Parámetros de cobro vigentes (config): los usa la pantalla; el servidor aplica la misma regla. */
export interface ParametrosCobroDTO {
  igtf_pct: string;
  /** Tasa BCV vigente: con ella se expresa en Bs el IGTF del cobro (importe fiscal). */
  tasa_bcv: string;
  /** Mínimo de caracteres de la referencia de transferencia, pago móvil, Zelle y USDT (no se repiten nunca). */
  referencia_min_caracteres: number;
  /** Dígitos de la referencia del punto (no se repite en el día). */
  referencia_min_punto: number;
  /** Tope en $ de lo que un cliente paga en Bs en notas, por día. */
  nota_tope_bs_usd: string;
  /** Máximo de notas con parte en Bs por cliente y día (0 = sin límite). */
  nota_max_bs_veces_dia: number;
  /** Con ?cliente_id: su saldo a favor, en $ (null sin cliente). */
  saldo_a_favor: string | null;
  /**
   * Con ?cliente_id: la parte de su saldo a favor que entró en divisas sin IGTF (abonos desde el 2026-10-19). Se gasta
   * primero y, en una factura o nota de débito, paga IGTF como divisas; el resto del saldo no (pedido de la dueña).
   */
  saldo_divisa_sin_igtf: string | null;
}

/** Lo que el cliente ya pagó hoy en bolívares en notas de entrega (para el tope por cliente y día). */
export interface BsNotasHoyDTO {
  /** $ ya pagados en Bs hoy en notas. */
  usado_usd: string;
  /** $ que aún puede pagar en Bs hoy. */
  disponible_usd: string;
  /** Notas de hoy con parte en Bs. */
  notas: number;
  /** Máximo de notas con Bs por día (0 = sin límite). */
  maximo_notas: number;
  /** false = ya llegó al máximo de notas con Bs de hoy. */
  admite_otra: boolean;
}

export interface PagoDTO {
  id: string;
  tipo: 'cobro' | 'abono' | 'reintegro';
  recibido_en: string;
  numero_formateado: string | null;
  total_usd: string;
  vuelto_usd: string;
  vuelto_bs: string;
  /** Cómo salió el vuelto en Bs (null si no hubo). */
  vuelto_bs_metodo: MetodoVueltoBs | null;
  vuelto_bs_referencia: string | null;
  /** Lo que quedó en caja porque no se devolvió todo el excedente, en $. */
  sobrante_usd: string;
  /** IGTF percibido en este cobro (pagos en divisa de FAC/ND): %, base y monto en $ (auxiliar de cobranza). */
  igtf_pct: string;
  igtf_base_usd: string;
  igtf_usd: string;
  /** Importe FISCAL del IGTF y de su base, en Bs a la tasa BCV del día del cobro (lo que va al libro y a la declaración). */
  igtf_bs: string;
  igtf_base_bs: string;
  /** IGTF y su base en la divisa recibida; tasa BCV del día del cobro (igtf_bs = igtf_divisa × tasa). */
  igtf_base_divisa: string;
  igtf_divisa: string;
  tasa_bcv_cobro: string | null;
  usuario_nombre: string;
  lineas: PagoLineaDTO[];
}

/** Recibo de un abono (anticipo): el pago con su cliente y su tienda, para imprimirlo y consultarlo después. */
export interface ReciboAbonoDTO extends PagoDTO {
  cliente_id: string;
  cliente_rif: string;
  cliente_razon_social: string;
  sucursal_id: string;
  sucursal_nombre: string;
  caja_nombre: string | null;
  /**
   * Lo que este abono sumó al saldo a favor, en $: todo lo recibido menos el vuelto (el abono no lleva IGTF: pedido de
   * la dueña 2026-10-19). Los abonos anteriores descontaban además su IGTF, que el recibo sigue mostrando.
   */
  abonado_usd: string;
  /** Saldo a favor del cliente justo después de este abono, en $. */
  saldo_despues_usd: string | null;
}

export interface RetencionDTO {
  id: string;
  tipo: 'iva' | 'municipal';
  modalidad: 'base25' | 'completo';
  base: string;
  porcentaje: string;
  monto: string;
  /** Base y monto fiscales en Bs (lo que dice el comprobante del agente de retención). */
  base_bs: string;
  monto_bs: string;
  estado: string;
  comprobante_numero: string | null;
  comprobante_fecha: string | null;
}

export interface DocumentoDTO {
  id: string;
  sucursal_id: string;
  sucursal_nombre: string;
  sucursal_formato_impresion: 'carta' | 'media_carta' | 'media_carta_vertical';
  /** Sucursal emisora (cabecera de los formatos) y plantilla del papel de factura, ya con sus medidas de partida. */
  sucursal_direccion_fiscal: string;
  sucursal_telefono: string | null;
  sucursal_plantilla: PlantillaFactura;
  tipo: 'FAC' | 'NOTA' | 'NC' | 'ND' | 'CAM';
  numero_formateado: string;
  control_formateado: string | null;
  documento_origen_id: string | null;
  documento_origen_numero: string | null;
  /** Fecha y total en Bs del documento afectado (los exige la nota de crédito o de débito impresa). */
  documento_origen_emitido_en: string | null;
  documento_origen_total_bs: string | null;
  /** IGTF en Bs percibido al emitir el documento afectado (el que salió impreso en su total). */
  documento_origen_igtf_bs: string | null;
  cliente_id: string;
  cliente_rif: string;
  cliente_razon_social: string;
  cliente_direccion_fiscal: string;
  /** Teléfono actual del cliente (no se congela con el documento). */
  cliente_telefono: string | null;
  cliente_tipo_contribuyente: string;
  cliente_municipal: boolean;
  lista_codigo: string;
  cajero_nombre: string;
  condicion: 'contado' | 'credito';
  emitido_en: string;
  vence_en: string | null;
  tasa_bcv: string;
  tasa_paralela: string;
  factor_divisa: string;
  tasa_aplicada: string;
  /**
   * FACTURA de contado: con qué regla salió (2026-10-15). bs = todo en bolívares a tasa 2 · divisa = precio real a
   * tasa BCV (pagó con divisas y el resto no pasó del tope) · bcv = precio a BCV (el resto pasó del tope). null = otro.
   */
  modo_precio: 'bs' | 'divisa' | 'bcv' | null;
  bruto: string;
  descuento: string;
  descuento_pct: string;
  base: string;
  exento: string;
  iva_pct: string;
  iva: string;
  total: string;
  /** Bolívares fiscales guardados por línea (migración 018): base_bs + exento_bs + iva_bs = total_bs. */
  bruto_bs: string;
  descuento_bs: string;
  base_bs: string;
  exento_bs: string;
  iva_bs: string;
  total_bs: string;
  /** Bs a cobrar: total_bs menos retenciones descontadas al cobrar (base25). */
  por_cobrar_bs: string;
  /**
   * Tasa Bs/$ con la que se cobra HOY el saldo pendiente (null si no hay saldo): al contado los Bs del papel; a crédito,
   * NOTA a la tasa 2 del día y FAC al "$ a BCV" congelado × BCV del día (regla P2 de la clienta).
   */
  tasa_bs_cobro_hoy: string | null;
  /** NOTA con saldo: lo que el cliente aún puede pagar hoy en Bs (tope por cliente y día). */
  bs_notas: BsNotasHoyDTO | null;
  modalidad_retencion: 'base25' | 'completo' | null;
  retencion_iva: string;
  retencion_municipal: string;
  /** Retenciones del cliente en Bs (importe fiscal). */
  retencion_iva_bs: string;
  retencion_municipal_bs: string;
  por_cobrar: string;
  pagado: string;
  saldo_pendiente: string;
  estado_pago: 'pendiente' | 'parcial' | 'pagado';
  estado_entrega: 'pendiente' | 'parcial' | 'entregado' | 'no_aplica';
  tiene_devolucion: boolean;
  impresiones: number;
  observaciones: string | null;
  /** `anulado` = fila en cero que conserva un número y un control anulados por hoja dañada. */
  estado: 'emitido' | 'anulado';
  anulado_en: string | null;
  anulado_por_nombre: string | null;
  motivo_anulacion: string | null;
  /** Fila anulada → documento vivo que la sustituye. */
  reemitido_como: { id: string; numero_formateado: string; control_formateado: string | null } | null;
  /** Documento vivo → números anteriores que quedaron anulados (el más reciente primero). */
  anulados: { id: string; numero_formateado: string; control_formateado: string | null; anulado_en: string; motivo: string }[];
  /**
   * `traspasado`: de lo cerrado de la línea (`entregado`), lo que no se entregó aquí sino que pasó pendiente a la factura
   * o nota nueva de «Facturar a otro nombre» (spec 08, enmienda 2026-10-29).
   */
  lineas: (LineaCalculadaDTO & { id: string; entregado: string; devuelto: string; pendiente_entrega: string; traspasado?: string })[];
  /** Combos y recetas con que se vendió (foto del combo, spec 20). */
  combos: ComboEnDocumentoDTO[];
  pagos: PagoDTO[];
  retenciones: RetencionDTO[];
  /** Documentos que nacen de este (p. ej. la nota de despacho de un CAMBIO directo). */
  derivados: { id: string; tipo: string; numero_formateado: string }[];
  /** De dónde salió el saldo a favor con que se pagó (solo pantalla; respuesta de la dueña del 26-10-2026). */
  saldo_origen?: OrigenSaldoDTO[];
  /** «Facturar a otro nombre» (spec 08, enmienda 2026-10-29): el documento original al que sustituye esta factura o nota. */
  sustituye?: { id: string; tipo: string; numero_formateado: string; cliente_razon_social: string } | null;
  /** El documento nuevo, a otro nombre, que sustituyó a este (con la nota de crédito o el Cambio que lo revirtió). */
  sustituida_por?: { id: string; tipo: string; numero_formateado: string; cliente_razon_social: string } | null;
}

/**
 * Abono, nota de crédito o reintegro del que salió (en orden de llegada) el saldo a favor usado en un documento. Es
 * informativo: el saldo es un fondo del cliente y un pago no queda ligado a un abono.
 */
export interface OrigenSaldoDTO {
  /** `traspaso`: saldo que otro cliente le pasó (de una nota de crédito suya, spec 03, enmienda 2026-10-29). */
  tipo: 'abono' | 'devolucion' | 'reintegro' | 'traspaso';
  referencia_tipo: string;
  referencia_id: string;
  /** Recibo del abono o número de la nota de crédito o del cambio. */
  numero: string | null;
  fecha: string;
  monto_original: string;
  /** Lo que de ese ingreso se usó en este documento, en $. */
  monto_usado: string;
  /** Con qué se pagó el abono (método, moneda, monto y referencia). */
  metodos: { metodo: string; moneda: 'USD' | 'BS'; monto: string; referencia: string | null }[];
}

export interface DocumentoResumenDTO {
  id: string;
  tipo: string;
  numero_formateado: string;
  control_formateado: string | null;
  cliente_razon_social: string;
  cliente_rif: string;
  emitido_en: string;
  condicion: string;
  total: string;
  total_bs: string;
  tasa_aplicada: string;
  saldo_pendiente: string;
  estado_pago: string;
  estado_entrega: string;
  cajero_nombre: string;
  estado: 'emitido' | 'anulado';
}

export interface EsperaDTO {
  id: string;
  nombre: string;
  cajero_nombre: string;
  creado_en: string;
  payload: EsperaInput['payload'];
  lineas_count: number;
}
